import { StandardEvents } from '@shopify/events';

// The custom header is outside Horizon's native cart-icon component.
// Use the completed cart result, never the added quantity or an assumed +1.
if (!window.wetroomCartCounterInstalled) {
  window.wetroomCartCounterInstalled = true;
  let revision = 0;
  let readController = null;
  let pendingChanges = 0;
  let overlappingChanges = false;
  let lastCount = null;

  const isCount = (value) => Number.isSafeInteger(value) && value >= 0;

  function renderCount(count, announce = false) {
    if (!isCount(count)) return;
    let changed = false;
    document.querySelectorAll('[data-wr-cart-count]').forEach((badge) => {
      if (badge.textContent.trim() !== String(count)) {
        badge.textContent = String(count);
        changed = true;
      }
    });
    const label = `Basket, ${count} ${count === 1 ? 'item' : 'items'}`;
    document.querySelectorAll('[data-wr-cart-link]').forEach((link) => {
      link.setAttribute('aria-label', label);
    });
    if (announce && changed) {
      document.querySelectorAll('[data-wr-cart-status]').forEach((status) => {
        status.textContent = label;
      });
    }
    lastCount = count;
  }

  function invalidateRead() {
    revision += 1;
    readController?.abort();
    readController = null;
  }

  async function refreshCount(announce = false) {
    if (pendingChanges > 0) return;
    invalidateRead();
    const currentRevision = revision;
    const controller = new AbortController();
    readController = controller;
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      const cart = await response.json();
      if (currentRevision === revision && pendingChanges === 0) {
        renderCount(cart.item_count, announce);
      }
    } catch (error) {
      // Retain the last known count on a network failure; never reset it to zero.
      if (error?.name !== 'AbortError') console.warn('[wetroom-cart-counter]', error);
    } finally {
      window.clearTimeout(timeout);
      if (readController === controller) readController = null;
    }
  }

  function onCartChange(event) {
    pendingChanges += 1;
    if (pendingChanges > 1) overlappingChanges = true;
    invalidateRead();
    // Standard cart events fire BEFORE the request. Wait for its promise.
    Promise.resolve(event.promise)
      .then((result) => {
        pendingChanges -= 1;
        if (pendingChanges > 0) return;
        const mustReconcile = overlappingChanges;
        overlappingChanges = false;
        const count = result?.cart?.totalQuantity ?? result?.cart?.item_count;
        if (!mustReconcile && isCount(count)) {
          renderCount(count, true);
        } else {
          // Reconcile concurrent requests or events without a complete cart.
          void refreshCount(true);
        }
      })
      .catch(() => {
        pendingChanges -= 1;
        if (pendingChanges === 0) {
          overlappingChanges = false;
          void refreshCount(false);
        }
      });
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartChange);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) void refreshCount();
  });
  window.addEventListener('focus', () => {
    if (document.visibilityState === 'visible') void refreshCount();
  });
  document.addEventListener('shopify:section:load', () => {
    if (lastCount !== null) renderCount(lastCount);
    void refreshCount();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void refreshCount(), { once: true });
  } else {
    void refreshCount();
  }
}
